import { type DragEvent, type ReactNode, type RefCallback, useCallback, useRef, useState } from 'react'
import { isDefined } from '@terros/common'
import { Tooltip } from '@mantine/core'
import { type MaterialSymbolType, SizePrimitives } from '@front/model'
import { MaterialSymbolIcon } from '../icon/MaterialSymbolIcon'
import { type TDataTableColumn, type TDataTableProps } from './types'
import styles from './table.module.css'

type Props<T extends Record<string, unknown> | unknown[]> = Pick<
  TDataTableProps<T>,
  'sortStatus' | 'onSortStatusChange' | 'pinFirstColumn' | 'columns' | 'setColumnWidth' | 'onColumnReorder'
> & {
  firstCellRef?: RefCallback<HTMLDivElement>
}

export const Header = <T extends Record<string, unknown> | unknown[]>({
  columns: effectiveColumns,
  sortStatus,
  onSortStatusChange,
  pinFirstColumn,
  setColumnWidth,
  onColumnReorder,
  firstCellRef,
}: Props<T>): ReactNode => {
  const dragColumnRef = useRef<TDataTableColumn<T> | null>(null)
  const [dragOverAccessor, setDragOverAccessor] = useState<string | null>(null)
  const [dragSide, setDragSide] = useState<'left' | 'right' | null>(null)

  const handleColumnResize = (e: React.MouseEvent<HTMLDivElement>, columnKey: string): void => {
    if (!setColumnWidth) return
    const startX = e.clientX
    const startWidth = effectiveColumns.find((c) => c.accessor === columnKey)?.width ?? 0

    const onMouseMove = (moveEvent: MouseEvent): void => {
      const newWidth = Math.max(100, startWidth + (moveEvent.clientX - startX))
      setColumnWidth(columnKey, newWidth)
    }

    const onMouseUp = (): void => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, column: TDataTableColumn<T>): void => {
    dragColumnRef.current = column
    e.dataTransfer.effectAllowed = 'move'
    e.currentTarget.dataset.dragging = 'true'
  }, [])

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, column: TDataTableColumn<T>): void => {
      e.preventDefault()
      setDragOverAccessor(column.accessor)
      if (dragColumnRef.current) {
        const fromIndex = effectiveColumns.findIndex((c) => c.accessor === dragColumnRef.current?.accessor)
        const toIndex = effectiveColumns.findIndex((c) => c.accessor === column.accessor)
        setDragSide(fromIndex < toIndex ? 'right' : 'left')
      }
    },
    [effectiveColumns]
  )

  const handleDragEnd = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      e.currentTarget.dataset.dragging = 'false'
      if (dragColumnRef.current && dragOverAccessor) {
        onColumnReorder?.(dragColumnRef.current.accessor, dragOverAccessor)
      }
      dragColumnRef.current = null
      setDragOverAccessor(null)
      setDragSide(null)
    },
    [dragOverAccessor, onColumnReorder]
  )

  const handleSortChange = useCallback(
    (column: TDataTableColumn<T>) => {
      if (!column.sortable || !onSortStatusChange) return

      const columnAccessor = column.accessor as keyof T | (string & NonNullable<unknown>)

      if (!sortStatus || sortStatus.columnAccessor !== columnAccessor) {
        onSortStatusChange({ columnAccessor, direction: 'asc' })
      } else {
        onSortStatusChange({
          columnAccessor,
          direction: sortStatus.direction === 'asc' ? 'desc' : 'asc',
        })
      }
    },
    [sortStatus, onSortStatusChange]
  )

  const renderSortIcon = useCallback(
    (column: TDataTableColumn<T>) => {
      if (!column.sortable) return null

      const isSorted = sortStatus && sortStatus.columnAccessor === column.accessor
      const icon: MaterialSymbolType = !isSorted
        ? 'swap_vert'
        : sortStatus.direction === 'asc'
          ? 'arrow_upward'
          : 'arrow_downward'
      return <MaterialSymbolIcon icon={icon} size={SizePrimitives[4]} color='var(--mantine-color-dimmed)' />
    },
    [sortStatus]
  )

  return (
    <div className={styles.headerRow}>
      {effectiveColumns.map((column, index) => {
        const canDrag = column.draggable && isDefined(onColumnReorder)
        const isDropTarget = dragOverAccessor === column.accessor && dragColumnRef.current?.accessor !== column.accessor
        const title = column.title || column.accessor

        return (
          <div
            ref={index === 0 ? firstCellRef : undefined}
            className={styles.headerCell}
            key={`header-${column.accessor}`}
            onClick={column.sortable ? () => handleSortChange(column) : undefined}
            data-align={column.textAlign}
            data-no-wrap={column.noWrap}
            data-pin-first={pinFirstColumn}
            data-sortable={column.sortable}
            data-resizable={column.resizable}
            draggable={canDrag}
            data-draggable={canDrag}
            data-drop-left={(isDropTarget && dragSide === 'left') || undefined}
            data-drop-right={(isDropTarget && dragSide === 'right') || undefined}
            onDragStart={canDrag ? (e) => handleDragStart(e, column) : undefined}
            onDragOver={canDrag ? (e) => handleDragOver(e, column) : undefined}
            onDragEnd={canDrag ? handleDragEnd : undefined}
          >
            {canDrag && (
              <div className={styles.dragHandle}>
                <MaterialSymbolIcon
                  icon='drag_indicator'
                  size={SizePrimitives[4]}
                  color='var(--mantine-color-dimmed)'
                />
              </div>
            )}
            <Tooltip label={title} withArrow>
              <div className={styles.headerContent}>
                {column.renderTitle ? column.renderTitle() : toHeaderTitleWithBreakpoints(title)}
              </div>
            </Tooltip>
            {column.sortable && (
              <div data-active={sortStatus?.columnAccessor === column.accessor} className={styles.sortIcon}>
                {renderSortIcon(column)}
              </div>
            )}
            {column.resizable && (
              <div className={styles.resizeHandle} onMouseDown={(e) => handleColumnResize(e, column.accessor)} />
            )}
          </div>
        )
      })}
    </div>
  )
}

const toHeaderTitleWithBreakpoints = (title: string): string => {
  return title.replaceAll(/([/+-])/g, '$1\u200b')
}
